import { supabase } from "@/integrations/supabase/client";

/** Detect device type from screen width */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/** Parse basic browser/OS from user agent */
export function parseBrowserOS(): { browser: string; os: string } {
  if (typeof navigator === 'undefined') return { browser: 'unknown', os: 'unknown' };
  const ua = navigator.userAgent;
  let browser = 'Other';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('OPR/') || ua.includes('Opera/')) browser = 'Opera';
  else if (ua.includes('Chrome/') && !ua.includes('Edg/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome/')) browser = 'Safari';

  let os = 'Other';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('CrOS')) os = 'ChromeOS';

  return { browser, os };
}

const DEVICE_ID_KEY = 'cctvp_device_id';

/** Stable per-browser device id (localStorage). Empty string if storage is unavailable (SSR/private mode). */
export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return ''; // private mode / storage blocked — skip device registration
  }
}

/**
 * Register this device for the per-account device limit (anti credential-sharing).
 * STAGE 1 = MONITOR: this never blocks; the RPC always allows and only reports
 * would_block. Fire-and-forget; must never break auth.
 */
export const DEVICE_LIMIT_EVENT = 'cctvp:device-limit';

export async function registerDevice(userId: string): Promise<Record<string, unknown> | null> {
  try {
    const deviceId = getDeviceId();
    if (!deviceId) return null;
    const deviceType = getDeviceType();
    const { browser, os } = parseBrowserOS();
    // Use screen.* (physical resolution, stable) not innerWidth (window size, noisy).
    const sw = typeof screen !== 'undefined' ? screen.width : null;
    const sh = typeof screen !== 'undefined' ? screen.height : null;
    const fingerprint = `${deviceType}|${os}|${sw ?? '?'}x${sh ?? '?'}`;
    const label = `${browser} · ${os} · ${sw ?? '?'}×${sh ?? '?'}`;
    const { data, error } = await supabase.rpc('register_device', {
      p_device_id: deviceId,
      p_fingerprint: fingerprint,
      p_label: label,
    });
    if (error) { console.debug('register_device failed:', error); return null; }
    const r = (data as Record<string, unknown>) ?? null;
    // Broadcast the result so the DeviceLimitGate can show/hide the block modal.
    // (When enforce=false, allowed is always true → the gate never blocks.)
    if (typeof window !== 'undefined' && r) {
      window.dispatchEvent(new CustomEvent(DEVICE_LIMIT_EVENT, { detail: r }));
    }
    return r;
  } catch (err) {
    console.debug('register_device error:', err);
    return null;
  }
}

/** Log login session with device info (fire-and-forget) */
export function logLoginSession(userId: string) {
  try {
    const deviceType = getDeviceType();
    const { browser, os } = parseBrowserOS();
    supabase
      .from('login_sessions')
      .insert({
        user_id: userId,
        device_type: deviceType,
        browser,
        os,
        screen_width: typeof window !== 'undefined' ? window.innerWidth : null,
        screen_height: typeof window !== 'undefined' ? window.innerHeight : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      // login_sessions table is not in generated Supabase types
      } as Record<string, unknown>)
      .then(() => {})
      .catch((err: unknown) => console.debug('Login session log failed:', err));
  } catch {
    // Silent fail — logging should never break auth
  }
}
